'use client';

import { servicesConfig } from '@/features/services/services.config';
import { useT } from 'next-i18next/client';
import Image from 'next/image';
import React, { type FC } from 'react';

type ServiceItemKeys =
  | 'services:services.service_item_0_heading'
  | 'services:services.service_item_0_subheading'
  | 'services:services.service_item_0_image_alt'
  | 'services:services.service_item_1_heading'
  | 'services:services.service_item_1_subheading'
  | 'services:services.service_item_1_image_alt';

type CaseStudyItem = {
  id: 0 | 1;
  imageSrc: string;
  translationKeys: {
    heading: ServiceItemKeys;
    subheading: ServiceItemKeys;
    imageAlt: ServiceItemKeys;
  };
};

type CaseStudyType = Omit<CaseStudyItem, 'translationKeys'> & {
  heading: string;
  subheading: string;
  imageAlt: string;
};

type CaseStudyItemProps = {
  caseStudy: CaseStudyType;
  index: number;
};

const CASE_STUDIES: CaseStudyItem[] = [
  {
    id: 0,
    imageSrc: '/images/web-development.png',
    translationKeys: {
      heading: 'services:services.service_item_0_heading',
      subheading: 'services:services.service_item_0_subheading',
      imageAlt: 'services:services.service_item_0_image_alt',
    },
  },
  {
    id: 1,
    imageSrc: '/images/app-development.png',
    translationKeys: {
      heading: 'services:services.service_item_1_heading',
      subheading: 'services:services.service_item_1_subheading',
      imageAlt: 'services:services.service_item_1_image_alt',
    },
  },
];

const CaseStudyItem: FC<CaseStudyItemProps> = ({ caseStudy, index }) => {
  const isImageFirst = index % 2 !== 0;

  return (
    <div className="lg:flex lg:justify-between lg:items-center py-16">
      <div className="lg:pr-8">
        <div className="pb-16 lg:max-w-lg">
          <h2 className="text-base/7 font-semibold text-[#F24E1E]">
            {caseStudy.heading}
          </h2>
          <p className="mt-2 text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            {caseStudy.subheading}
          </p>
        </div>
      </div>
      <Image
        src={caseStudy.imageSrc}
        className={`-ml-4 max-w-80 sm:w-[57rem] sm:max-w-md md:max-w-2xl lg:-ml-0 ${isImageFirst ? 'md:ml-8' : 'md:ml-20 lg:-mr-32'}`}
        alt={caseStudy.imageAlt}
        loading={'eager'}
        width={785}
        height={814}
      />
    </div>
  );
};

export const CaseStudiesBlock: FC = () => {
  const { t } = useT(servicesConfig.i18nNamespaces);

  const caseStudiesData: CaseStudyType[] = CASE_STUDIES.map(
    ({ id, imageSrc, translationKeys }) => ({
      id,
      imageSrc,
      heading: t(translationKeys.heading),
      subheading: t(translationKeys.subheading),
      imageAlt: t(translationKeys.imageAlt),
    })
  );

  return (
    <section className="bg-[#0A0A0A] py-24 md:py-[96px]">
      <div className="container">
        {caseStudiesData.map((caseStudy, index) => (
          <CaseStudyItem key={caseStudy.id} caseStudy={caseStudy} index={index} />
        ))}
      </div>
    </section>
  );
};
